import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import { graphql, compose } from 'react-apollo';
import _ from 'lodash';

import Spinner from '../../spinner/spinner';
import { getChurch } from './queries/getChurch';
import { updateChurch } from "./mutations/updateChurch";
import { resendPastorConfirmation } from "./mutations/resendPastorConfirmation";
import { deactivateUser } from "../../../graphql/mutations/deactivateUser";
import { Form, Button, Row, Col, Modal } from 'antd';
import displayServerErrors from '../../../utilities/form/display_server_errors';
import ChurchForm from "./form/churchForm";
import { PastorCard } from '../../common/pastorCard/pastorCard';
import {ROLE_SETS, ROLES} from "../../authorization/roles";
import { AVAILABLE_TIMEZONES } from "./constants";
import { deactivateChurch } from "./mutations/deactivateChurch";

const REVIVAL_PREPARATION_PHASE = 1;
const REVIVAL_PHASE = 2;
const REVIVAL_FINISH_PHASE = 3;

class ChurchEditPage extends Component {
  state = {
    loading: false
  }

  get church() {
    return this.props.data.church;
  }

  get pastors() {
    return this.church.pastors.edges.map(edges => edges.node);
  }

  get pastorsList() {
    return([this.church.seniorPastor].concat(this.pastors));
  }

  get preprocessedFieldsValue() {
    let values = {
      ...this.props.form.getFieldsValue(),
      id: parseInt(this.props.match.params.churchId, 10)
    }

    if (values["add_revival"]) {
      values.revival = this.revivalParams;
    } else {
      delete values["revival"]
    }

    return values;
  }

  get revivalParams() {
    const {getFieldValue} = this.props.form;

    if (!getFieldValue('add_revival')) return null

    return {
      title: getFieldValue('revival.title'),
      image_data_uri: getFieldValue('revival.image_data_uri'),
      preparation_start_date: _.inRange(this.church.revival.phase, REVIVAL_PREPARATION_PHASE, REVIVAL_PHASE) ? undefined : getFieldValue('revival.preparation_start_date').unix(),
      start_date: _.inRange(this.church.revival.phase, REVIVAL_PREPARATION_PHASE, REVIVAL_PHASE) ? undefined : getFieldValue('revival.start_date').unix(),
      end_date: this.church.revival.phase == REVIVAL_PHASE ? undefined : getFieldValue('revival.end_date').unix()
    };
  }

  get updateMutationParams() {
    return {
      variables: {
        input: this.preprocessedFieldsValue
      }
    };
  }

  get renderChurchCodeField() {
    return <Form.Item label="Church code">{this.church.code}</Form.Item>;
  }

  get renderTimezoneField() {
    const timezone = AVAILABLE_TIMEZONES.find((timezone) => timezone.value === this.church.timezone);

    return <Form.Item label="Timezone">{timezone ? timezone.name : this.church.timezone}</Form.Item>;
  }

  get renderEvangelistsField() {
    const evangelist = this.church.evangelist;

    return (
      <Form.Item label="Evangelist">
        <div>{evangelist.name}</div>
        <div>{evangelist.phone}</div>
        <div>{evangelist.email}</div>
      </Form.Item>
    );
  }

  get renderPastorsList() {
    return (
      <div>
        {
          this.pastorsList.map((pastor, index) =>
            <Row key={index} >
              <Col span={22}>
                <PastorCard pastor={this.decoratePastor(pastor)} />
              </Col>
              <Col span={2}>
                { ROLE_SETS.pastors.includes(pastor.type) &&
                    <div>
                      { !pastor.passwordCreated &&
                          <Button onClick={() => {
                            this.onResendConfirmationClick(pastor.id, pastor.email)
                          }} type="primary">
                            Re-send invitation letter
                          </Button>
                      }
                      <Button onClick={() => { this.onDeleteClick(pastor.id, pastor.name) }} type="primary">
                        Delete pastor
                      </Button>
                    </div>
                }
              </Col>
            </Row>
          )
        }
      </div>
    );
  }

  get renderGroupsField() {
    return (
      <span>
        Please refer to <u><b>Church Profile - Groups</b></u> to manage Church Groups (log in as evangelist)
      </span>
    );
  }

  get newPastorsEmails() {
    const pastors = this.props.form.getFieldValue('pastors');
    return pastors ? Object.values(pastors).map((pastor) => pastor.email) : [];
  }

  get validationKeys() {
    return { revival: ['title', 'preparation_start_date', 'start_date', 'end_date'], pastors: ['name', 'email'] };
  }

  deactivatePastorParams(pastorId) {
    return {
      variables: {
        input: {
          id: pastorId
        }
      },
      refetchQueries: [{
        query: getChurch,
        variables: { churchId: parseInt(this.props.match.params.churchId, 10) }
      }]
    }
  }

  resendPastorConfirmationParams(pastorId) {
    return {
      variables: {
        input: {
          id: pastorId
        }
      }
    }
  }

  deactivateChurchParams() {
    return {
      variables: {
        input: {
          id: this.church.id
        }
      }
    }
  }

  saveConfirmationText(emails) {
    return (
      <div>
        <p>Church profile changes saved</p>
        { emails.length > 0 &&
          <div>
            <p>Confirmation emails were sent to:</p>
            <ul>
              {
                emails.map((email, index) => {
                  return <li key={index}>{email}</li>
                })
              }
            </ul>
          </div>
        }
      </div>
    );
  }

  async submitData() {
    try {
      this.setState({ loading: true });
      const emails = this.newPastorsEmails;
      await this.props.updateChurch(this.updateMutationParams);
      this.props.history.push('/dashboard/admin/churches')
      Modal.success({
        title: this.saveConfirmationText(emails)
      });
    } catch(response) {
      displayServerErrors(this.props.form, response, this.validationKeys);
    } finally {
      this.setState({ loading: false });
    }
  }

  onDeleteClick = (pastorId, pastorName) => {
    Modal.confirm({
      title: `Are you sure you want to delete profile of ${pastorName}?`,
      okText: "Delete",
      width: 600,
      onOk: () => this.props.deactivatePastor(this.deactivatePastorParams(pastorId))
    });
  };

  onDeactivateChurchClick = () => {
    Modal.confirm({
      title: 'Archive Church',
      content: 'Are you sure you want to archive this church? This action is irreversible.',
      okText: "Archive",
      okType: 'danger',
      width: 600,
      onOk: () => {
        this.setState({ loading: true });
        this.props.deactivateChurch(this.deactivateChurchParams()).then((response) => {
          this.setState({ loading: false });
          if(response.data.deactivateChurch) {
            Modal.info({ content: "Church has been archived." });
            this.props.history.push('/dashboard/admin/churches');
          } else {
            Modal.error({ content: "Failed to archive church." });
          }
        }).catch((response) => {
          this.setState({ loading: false });
          Modal.error({ content: response.toString() });
        })
      }
    });
  };

  onResendConfirmationClick = (pastorId, pastorEmail) => {
    this.props.resendPastorConfirmation(this.resendPastorConfirmationParams(pastorId)).then((response) => {
      if(response && response.data && response.data.resendPastorConfirmation) {
        Modal.success({ content: `Invitation has been re-sent to ${pastorEmail}` });
      } else {
        Modal.info({
          content: `Invitation has not been re-sent to ${pastorEmail} as this user does not exist or he has already accepted an invitation`
        });
      }
    });
  };

  onSubmit = (event) => {
    event.preventDefault();

    this.props.form.validateFields((error) => {
      if (!error) {
        this.submitData();
      }
    });
  };

  decoratePastor = (pastor) => {
    const roleLabel = pastor.type === ROLES.seniorPastor ? "SENIOR PASTOR" : null;
    return { ...pastor, roleLabel };
  };

  render() {
    if (this.props.data.loading || this.state.loading) {
      return <Spinner />;
    } else if (this.church) {
      return (
        <div>
          <div className="align-right">
            <Button onClick={() => { this.onDeactivateChurchClick() }} type="danger" size="large">
              Archive Church
            </Button>
          </div>
          <ChurchForm {...this.props} form={this.props.form}
                      church={this.church}
                      churchCodeField={this.renderChurchCodeField}
                      evangelistsField={this.renderEvangelistsField}
                      timezoneField={this.renderTimezoneField}
                      pastorsList={this.renderPastorsList}
                      groupsField={this.renderGroupsField}
                      onSubmit={this.onSubmit}/>
        </div>
      );
    } else {
      return <Redirect to="/404" />;
    }
  }
}

export default Form.create()(
  compose(
    graphql(getChurch, {
      options: ({ match }) => ({ variables: { churchId: parseInt(match.params.churchId, 10) } })
    }),
    graphql(updateChurch, { name: 'updateChurch' }),
    graphql(deactivateUser, { name: 'deactivatePastor' }),
    graphql(resendPastorConfirmation, { name: 'resendPastorConfirmation' }),
    graphql(deactivateChurch, { name: 'deactivateChurch' })
  )(ChurchEditPage)
);



// WEBPACK FOOTER //
// ./src/components/pages/churches/churchEdit.js